/**
 * Command palette logic (design/ux/01_foundations.md §6.5, WP-17;
 * AUD-NAV-06, AUD-NAV-20, OP-F-16).
 *
 *  · The page index is built from the navigation the role actually sees
 *    (every visible leaf, by its full path « Groupe › Page »), so the palette
 *    can never offer a page the menu hides: `buildLeafIndex(flattenNav(user))`
 *    since WP-18 (NAV_TREE, full paths, keywords); `buildPageIndex` keeps
 *    reading group-shaped menus.
 *  · Matching is accent- and case-insensitive (NFD, diacritics stripped —
 *    « tresorerie » finds « Trésorerie »), token-AND in any order over the
 *    label, the path and the leaf keywords; label prefixes rank first.
 *  · « Récents »: the last pages visited, per user, in localStorage behind a
 *    guarded storage (a private window never breaks the palette).
 *
 * Pure (no DOM, no React): the component is components/keyboard/CommandPalette.tsx.
 */
import { foldText, queryTokens } from "./combobox.js";

export const PALETTE_GROUPS = Object.freeze({
  RECENTS: "Récents",
  ACTIONS: "Actions",
  PAGES: "Pages",
  PRODUITS: "Produits",
  LOTS: "Lots",
});

/**
 * @typedef {Object} PalettePage
 * @property {string} value    the route (unique)
 * @property {string} label
 * @property {string} meta     full path « Groupe › Page »
 * @property {string} [keywords]
 * @property {string} [icon]
 * @property {string} group
 */

/**
 * @param {ReadonlyArray<{label?: string, items: ReadonlyArray<any>}>} groups navigation groups of the role
 * @param {(page: string) => string} toPath
 * @returns {PalettePage[]}
 */
export function buildPageIndex(groups, toPath) {
  const out = [];
  const seen = new Set();
  for (const g of groups ?? []) {
    for (const it of g.items ?? []) {
      if (!it?.page || it.locked) continue;
      const path = toPath(it.page);
      if (!path || seen.has(path)) continue;
      seen.add(path);
      out.push({
        value: path,
        label: it.label,
        meta: g.label ? `${g.label} › ${it.label}` : it.label,
        keywords: Array.isArray(it.keywords) ? it.keywords.join(" ") : (it.keywords ?? ""),
        icon: it.icon,
        group: PALETTE_GROUPS.PAGES,
      });
    }
  }
  return out;
}

/**
 * The page index from the navigation model (WP-18): every visible leaf of
 * the session by its full path (« Achats › Factures d'achat › Journal des
 * achats »), its palette label (`title`, else the page label) and keywords.
 * A locked page (module not subscribed) stays listed, as in the menu, and
 * says so; same output shape as `buildPageIndex`.
 * @param {ReadonlyArray<{to?: string, label: string, title?: string, path: string[], keywords?: string[], icon?: string, locked?: boolean}>} leaves  `flattenNav()` output
 * @returns {PalettePage[]}
 */
export function buildLeafIndex(leaves) {
  const out = [];
  const seen = new Set();
  for (const leaf of leaves ?? []) {
    const path = leaf?.to;
    if (!path || seen.has(path)) continue;
    seen.add(path);
    const meta = (leaf.path ?? [leaf.label]).join(" › ");
    out.push({
      value: path,
      label: leaf.title ?? leaf.label,
      meta: leaf.locked ? `${meta} · module non souscrit` : meta,
      keywords: Array.isArray(leaf.keywords) ? leaf.keywords.join(" ") : (leaf.keywords ?? ""),
      icon: leaf.icon,
      group: PALETTE_GROUPS.PAGES,
    });
  }
  return out;
}

function rank(page, tokens) {
  const label = foldText(page.label);
  const first = tokens[0];
  if (label.startsWith(first)) return 0;
  if (label.split(/[\s'’\-–—/()]+/).some((w) => w.startsWith(first))) return 1;
  return 2;
}

/**
 * The command verbs of the session (`PALETTE_VERBS`, navigation.config.js): a verb is kept only when
 * its `leaf` is a page of the role's index (the menu shows it — never a verb the role cannot reach).
 * Its target is the CURRENT page when that page hosts the verb's form (`hosts`; the other query
 * parameters stay, `replace` so Back does not land on the same page twice), else the leaf. Same
 * shape as the page index (`value` = the target, `meta`, `keywords`), matched by `matchPages`.
 * @param {ReadonlyArray<{ id: string, label: string, meta?: string, leaf: string, hosts?: ReadonlyArray<string>, param: Record<string, string>, icon?: string, keywords?: ReadonlyArray<string> }>} verbs
 * @param {PalettePage[]} pages the role's page index
 * @param {{ pathname?: string, search?: string }} [location]
 */
export function buildVerbIndex(verbs, pages, location = {}) {
  const visibles = new Set((pages ?? []).map((p) => p.value));
  const pathname = location.pathname ?? "";
  return (verbs ?? [])
    .filter((v) => visibles.has(v.leaf))
    .map((v) => {
      const ici = (v.hosts ?? []).includes(pathname);
      const q = new URLSearchParams(ici ? String(location.search ?? "").replace(/^\?/, "") : "");
      for (const [k, val] of Object.entries(v.param ?? {})) q.set(k, val);
      return {
        value: `${ici ? pathname : v.leaf}?${q.toString()}`,
        label: v.label,
        meta: v.meta ?? "",
        keywords: Array.isArray(v.keywords) ? v.keywords.join(" ") : (v.keywords ?? ""),
        icon: v.icon,
        group: PALETTE_GROUPS.ACTIONS,
        replace: ici,
      };
    });
}

/**
 * Pages matching the query (token-AND, accent-insensitive), best first.
 * @param {PalettePage[]} index
 * @param {string} query
 * @param {number} [limit]
 */
export function matchPages(index, query, limit = Infinity) {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return [];
  const hits = [];
  index.forEach((p, i) => {
    const hay = foldText(`${p.label} ${p.meta} ${p.keywords ?? ""}`);
    if (tokens.every((t) => hay.includes(t))) hits.push({ p, r: rank(p, tokens), i });
  });
  hits.sort((a, b) => a.r - b.r || a.i - b.i);
  return hits.slice(0, limit).map((h) => h.p);
}

// ── Récents (OP-F-16) ────────────────────────────────────────────────────────

export function recentsKey(prefix, userId) {
  return `${prefix}${userId ?? "anonyme"}`;
}

/** @returns {Array<{value: string, label: string, meta: string, icon?: string}>} */
export function readRecents(key, storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem(key);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v)
      ? v.filter((r) => r && typeof r.value === "string" && typeof r.label === "string")
      : [];
  } catch {
    return [];
  }
}

/**
 * Put the page first (deduplicated by route), keep `max`; returns the list.
 * Only pages of the index are recorded (never a detail route or an id).
 */
export function recordRecent(key, page, max, storage = globalThis.localStorage) {
  const current = readRecents(key, storage);
  if (!page?.value) return current;
  const entry = { value: page.value, label: page.label, meta: page.meta, ...(page.icon ? { icon: page.icon } : {}) };
  const next = [entry, ...current.filter((r) => r.value !== page.value)].slice(0, max);
  try {
    storage?.setItem(key, JSON.stringify(next));
  } catch {
    /* blocked storage: the list lives for this page only */
  }
  return next;
}

/**
 * The recents still visible to the role (a page removed from the menu, or a
 * role change on the workstation, drops out), in the index's current wording.
 */
export function visibleRecents(recents, index) {
  const byPath = new Map(index.map((p) => [p.value, p]));
  return recents
    .filter((r) => byPath.has(r.value))
    .map((r) => ({ ...byPath.get(r.value), group: PALETTE_GROUPS.RECENTS }));
}

/** The index page for a pathname (exact route match), or null. */
export function pageForPath(index, pathname) {
  return index.find((p) => p.value === pathname) ?? null;
}

/**
 * Where a product hit goes (§6.5): the product's own view for a role that
 * manages the catalogue, the product search page otherwise (CAISSIER — which
 * previously bounced silently to /caissier).
 * @param {{ id: string, label: string }} product
 * @param {boolean} managesCatalogue
 */
export function productTarget(product, managesCatalogue) {
  if (managesCatalogue) return `/produits/${encodeURIComponent(product.id)}`;
  return `/recherche-produits?q=${encodeURIComponent(product.label)}`;
}

// ─── « Lots » group (refonte WP-37 / cross-module XWP-12, OP-PSA-33) ─────────

const pliLot = (v) => String(v ?? "").trim().toLowerCase();

/**
 * The lot rows of `GET /stock/lots?search=q` that ARE the query — the N° lot (case-insensitive,
 * trimmed) or the lot label code — in the server's order, at most `limite`. The other rows of that
 * page (lots of products whose name matched) belong to the « Produits » group, not here.
 * @param {Array<{ id: string, numero_lot?: string, code_barre_lot?: { code?: string } }>} rows
 * @param {string} query
 * @param {number} limite
 */
export function lotsCorrespondants(rows, query, limite) {
  const q = pliLot(query);
  if (!q) return [];
  const brut = String(query ?? "").trim();
  return (Array.isArray(rows) ? rows : [])
    .filter((l) => pliLot(l?.numero_lot) === q || (l?.code_barre_lot?.code && String(l.code_barre_lot.code) === brut))
    .slice(0, limite);
}

/** Where a lot hit goes: the Stock & lots list narrowed on that N° lot (`?search=`, exact on the server). */
export function lotTarget(lot) {
  return `/stock?search=${encodeURIComponent(String(lot?.numero_lot ?? "").trim())}`;
}

/** « Produit · Pér. MM/AA · n u. » — the meta line of a lot hit (sellable units, XD-55 for a CAISSIER). */
export function lotMeta(lot) {
  const p = lot?.produit ?? {};
  const nom = [p.nom_commercial, p.dosage].filter(Boolean).join(" ");
  let per = null;
  if (lot?.date_peremption) {
    const d = new Date(lot.date_peremption);
    if (!Number.isNaN(d.getTime())) per = `Pér. ${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
  }
  const n = lot?.vendable ?? lot?.quantite_disponible;
  const qte = n != null ? `${n} u.` : null;
  return [nom || null, per, qte].filter(Boolean).join(" · ");
}
